import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../auth/AuthContext';
import { applyAppearance } from '../theme/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const SETTINGS_CACHE_KEY = 'bp_app_settings';

export const DEFAULT_SETTINGS = {
  appearance: { brand_color: '#059669', theme: 'light', font_scale: 100, show_logo_in_menu: false },
  os_defaults: { default_start_time: '08:00', default_duration_min: 120, default_return_days: 15 },
  payment_defaults: { cash_auto_enable: false, cash_method: 'pix_dinheiro', cash_discount_pct: 0, max_installments: 12, credit_fees: [3.15, 5.39, 6.12, 6.85, 7.57, 8.28, 8.99, 9.69, 10.38, 11.06, 11.74, 12.4], debit_fee: 1.37 },
  notifications: { os_overdue: true, ces_pending: true, low_stock: true, low_stock_threshold: 1, sst_expiry: true },
  locale: { language: 'pt-BR', timezone: 'America/Sao_Paulo', date_format: 'DD/MM/AAAA' },
  security: { idle_logout_min: 0, pwa_require_login: true },
};

export const getCachedSettings = () => {
  try { return { ...DEFAULT_SETTINGS, ...(JSON.parse(localStorage.getItem(SETTINGS_CACHE_KEY)) || {}) }; } catch { return DEFAULT_SETTINGS; }
};

const Ctx = createContext({ settings: DEFAULT_SETTINGS, company: null });
const ACTIVITY = ['pointerdown', 'keydown', 'scroll', 'touchstart'];

function useIdleLogout(minutes, active, logout) {
  useEffect(() => {
    if (!active || !minutes) return undefined;
    let t;
    const reset = () => { clearTimeout(t); t = setTimeout(() => { toast.info('Sessão encerrada por inatividade.'); logout(); }, minutes * 60000); };
    ACTIVITY.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(t); ACTIVITY.forEach(e => window.removeEventListener(e, reset)); };
  }, [minutes, active, logout]);
}

export function AppSettingsProvider({ children }) {
  const { user, logout } = useAuth();
  const [settings, setSettings] = useState(getCachedSettings);
  const [company, setCompany] = useState(null);

  const store = useCallback((s) => {
    setSettings(s);
    applyAppearance(s.appearance);
    try { localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(s)); } catch {}
  }, []);

  const reload = useCallback(async () => {
    const [s, c] = await Promise.allSettled([axios.get(`${API}/app-settings`), axios.get(`${API}/companies/me`)]);
    if (s.status === 'fulfilled') store(s.value.data);
    if (c.status === 'fulfilled') setCompany(c.value.data);
  }, [store]);

  useEffect(() => { applyAppearance(settings.appearance); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (user) reload(); }, [user, reload]);
  useEffect(() => {
    if (settings.appearance.theme !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const h = () => applyAppearance(settings.appearance);
    mq.addEventListener?.('change', h);
    return () => mq.removeEventListener?.('change', h);
  }, [settings.appearance]);
  useIdleLogout(settings.security.idle_logout_min, !!user, logout);

  const save = useCallback(async (s) => { const { data } = await axios.put(`${API}/app-settings`, s); store(data); return data; }, [store]);
  const preview = useCallback((appearance) => applyAppearance(appearance || settings.appearance), [settings.appearance]);

  const value = useMemo(() => ({ settings, company, save, preview, reload }), [settings, company, save, preview, reload]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAppSettings = () => useContext(Ctx);
